import { useState, useRef } from "react";
import { ShieldCheck, Zap, Database, RefreshCw, Activity, Lock } from "lucide-react";

export function GuardianHeroAnimation() {
  const [activeNode, setActiveNode] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 14; // -7 to +7 deg tilt
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -14; // -7 to +7 deg tilt
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 });
    setActiveNode(null);
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-full max-w-[580px] mx-auto select-none py-6 sm:py-8"
    >
      <style>{`
        @keyframes guardian-core-pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.08); opacity: 0.9; }
        }
        @keyframes guardian-laser-scan {
          0% { transform: translateY(-100%); opacity: 0; }
          30% { opacity: 0.85; }
          70% { opacity: 0.85; }
          100% { transform: translateY(220%); opacity: 0; }
        }
        @keyframes guardian-ring-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes guardian-ring-reverse {
          0% { transform: rotate(360deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes guardian-float-gentle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes guardian-signal-dash {
          0% { stroke-dashoffset: 40; }
          100% { stroke-dashoffset: 0; }
        }
      `}</style>

      {/* Ambient Atmospheric Orange Reactor Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 sm:w-96 h-80 sm:h-96 bg-gradient-to-tr from-[#FF5A1F]/20 via-orange-400/15 to-transparent blur-3xl rounded-full pointer-events-none" />

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 3D PERSPECTIVE REACTOR VIEWPORT                                     */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <div
        className="relative [perspective:1000px] flex items-center justify-center min-h-[460px] sm:min-h-[490px]"
        style={{
          transform: `rotateY(${mousePos.x}deg) rotateX(${mousePos.y}deg)`,
          transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* ────────────────────────────────────────────────────────────────── */}
        {/* SVG ORBITAL RINGS & CORNER TRACES (EXACT REFERENCE LAYOUT)         */}
        {/* ────────────────────────────────────────────────────────────────── */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-0"
          viewBox="0 0 580 480"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <radialGradient id="guardianCentralAura" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FF5A1F" stopOpacity="0.18" />
              <stop offset="65%" stopColor="#FFA07A" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#FF5A1F" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Central Soft Warm Circular Backdrop */}
          <circle cx="290" cy="240" r="145" fill="url(#guardianCentralAura)" />

          {/* Outer Dashed Orbit Ring */}
          <circle
            cx="290"
            cy="240"
            r="142"
            stroke="#FF5A1F"
            strokeWidth="1.2"
            strokeDasharray="4 4"
            strokeOpacity="0.32"
          />

          {/* Inner Delicate Solid Orbit Ring */}
          <circle
            cx="290"
            cy="240"
            r="118"
            stroke="#FF5A1F"
            strokeWidth="0.8"
            strokeOpacity="0.2"
          />

          {/* Node Dot 1: Top-Left Quadrant on Orbit Ring (~135 deg) */}
          <circle cx="190" cy="140" r="4.5" fill="#FF5A1F" />
          <circle cx="190" cy="140" r="2" fill="#FFFFFF" />

          {/* Node Dot 2: Bottom-Right Quadrant on Orbit Ring (~315 deg) */}
          <circle cx="390" cy="340" r="4.5" fill="#FF5A1F" />
          <circle cx="390" cy="340" r="2" fill="#FFFFFF" />

          {/* 4 Diagonal Dashed Traces from Corner Cards towards Center Ring */}
          {/* Top-Left Trace */}
          <line
            x1="180"
            y1="130"
            x2="236"
            y2="186"
            stroke="#FF5A1F"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            strokeOpacity={activeNode === "detect" ? "1" : "0.55"}
            style={{ animation: "guardian-signal-dash 2s linear infinite" }}
          />

          {/* Top-Right Trace */}
          <line
            x1="400"
            y1="130"
            x2="344"
            y2="186"
            stroke="#FF5A1F"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            strokeOpacity={activeNode === "heal" ? "1" : "0.55"}
            style={{ animation: "guardian-signal-dash 2s linear infinite reverse" }}
          />

          {/* Bottom-Left Trace */}
          <line
            x1="180"
            y1="350"
            x2="236"
            y2="294"
            stroke="#FF5A1F"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            strokeOpacity={activeNode === "isolate" ? "1" : "0.55"}
            style={{ animation: "guardian-signal-dash 2s linear infinite" }}
          />

          {/* Bottom-Right Trace */}
          <line
            x1="400"
            y1="350"
            x2="344"
            y2="294"
            stroke="#FF5A1F"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            strokeOpacity={activeNode === "recover" ? "1" : "0.55"}
            style={{ animation: "guardian-signal-dash 2s linear infinite reverse" }}
          />
        </svg>

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* CENTER GUARDIAN FAVICON / LOGO PORTAL (Z-10)                        */}
        {/* ────────────────────────────────────────────────────────────────── */}
        <div className="relative z-10 w-[170px] sm:w-[190px] aspect-square flex items-center justify-center">
          
          {/* Outer Rotating Concentric Cyber Shimmer Rings */}
          <div
            className="absolute inset-[-14px] rounded-full border border-orange-400/25 border-dashed pointer-events-none"
            style={{ animation: "guardian-ring-spin 35s linear infinite" }}
          />
          <div
            className="absolute inset-[-4px] rounded-full border border-[#FF5A1F]/30 pointer-events-none"
            style={{ animation: "guardian-ring-reverse 25s linear infinite" }}
          />

          {/* Central Floating Glassmorphic Disc with Guardian Favicon */}
          <div
            className="relative w-full h-full rounded-3xl bg-white/95 backdrop-blur-xl border border-neutral-200/90 shadow-2xl shadow-orange-500/20 flex flex-col items-center justify-center p-3 text-center group cursor-pointer"
            style={{ animation: "guardian-float-gentle 5s ease-in-out infinite" }}
          >
            {/* Top Active Defense Status Pill */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-black/90 backdrop-blur-md border border-white/20 text-white text-[9px] font-mono font-bold flex items-center gap-1 shadow-lg whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
              <span className="text-emerald-400">ACTIVE DEFENSE</span>
            </div>

            {/* Central Guardian Favicon Icon */}
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-1">
              <div className="absolute inset-0 bg-radial from-[#FF5A1F]/20 via-orange-400/10 to-transparent rounded-full blur-md" />
              <img
                src="/brand/guardian-favicon.png"
                alt="NeuraVolt Guardian Favicon"
                className="relative z-10 w-full h-full object-contain filter drop-shadow-[0_4px_12px_rgba(255,90,31,0.25)] transform group-hover:scale-110 transition-transform duration-500"
              />
            </div>

            {/* Guardian Brand Text */}
            <div className="text-[13px] sm:text-[14px] font-display font-black text-neutral-900 tracking-tight leading-none mb-0.5">
              Guardian
            </div>

            {/* Bottom Inscription Ribbon */}
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-white/95 backdrop-blur-md border border-neutral-200/90 shadow-md whitespace-nowrap">
              <span className="text-[8px] font-mono font-bold text-neutral-800 tracking-wider uppercase">
                ALWAYS ONE STEP AHEAD
              </span>
            </div>
          </div>
        </div>

        {/* ────────────────────────────────────────────────────────────────── */}
        {/* 4 INTERACTIVE CORNER CARDS (Z-20, NO LINES/DOTS OVERLAPPING TEXT)  */}
        {/* ────────────────────────────────────────────────────────────────── */}

        {/* Card 1: Threat Detection (Top-Left) */}
        <div
          onMouseEnter={() => setActiveNode("detect")}
          onMouseLeave={() => setActiveNode(null)}
          className={`absolute top-0 left-0 z-20 p-3 sm:p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border transition-all duration-300 text-left max-w-[195px] cursor-pointer ${
            activeNode === "detect"
              ? "border-[#FF5A1F] shadow-xl shadow-[#FF5A1F]/20 scale-105"
              : "border-neutral-200/90 shadow-md hover:shadow-lg hover:border-neutral-300"
          }`}
        >
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FF5A1F] grid place-items-center shrink-0">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-neutral-900 leading-tight">Threat Detection</div>
              <div className="text-[10px] text-neutral-500 font-medium">Real-time analysis</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-emerald-600 pl-1 pt-1 border-t border-black/5">
            <span>Latency &lt; 2.0s</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
          </div>
        </div>

        {/* Card 2: Self-Healing (Top-Right) */}
        <div
          onMouseEnter={() => setActiveNode("heal")}
          onMouseLeave={() => setActiveNode(null)}
          className={`absolute top-0 right-0 z-20 p-3 sm:p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border transition-all duration-300 text-left max-w-[195px] cursor-pointer ${
            activeNode === "heal"
              ? "border-[#FF5A1F] shadow-xl shadow-[#FF5A1F]/20 scale-105"
              : "border-neutral-200/90 shadow-md hover:shadow-lg hover:border-neutral-300"
          }`}
        >
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FF5A1F] grid place-items-center shrink-0">
              <Zap className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-neutral-900 leading-tight">Self-Healing</div>
              <div className="text-[10px] text-neutral-500 font-medium">Automated runbooks</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-blue-600 pl-1 pt-1 border-t border-black/5">
            <span>42+ Active Playbooks</span>
            <Activity className="w-3 h-3 text-blue-500" />
          </div>
        </div>

        {/* Card 3: Workload Isolation (Bottom-Left) */}
        <div
          onMouseEnter={() => setActiveNode("isolate")}
          onMouseLeave={() => setActiveNode(null)}
          className={`absolute bottom-0 left-0 z-20 p-3 sm:p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border transition-all duration-300 text-left max-w-[195px] cursor-pointer ${
            activeNode === "isolate"
              ? "border-[#FF5A1F] shadow-xl shadow-[#FF5A1F]/20 scale-105"
              : "border-neutral-200/90 shadow-md hover:shadow-lg hover:border-neutral-300"
          }`}
        >
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FF5A1F] grid place-items-center shrink-0">
              <Database className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-neutral-900 leading-tight">Workload Isolation</div>
              <div className="text-[10px] text-neutral-500 font-medium">Instant containment</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-purple-600 pl-1 pt-1 border-t border-black/5">
            <span>Lateral Lock</span>
            <Lock className="w-3 h-3 text-purple-500" />
          </div>
        </div>

        {/* Card 4: Rapid Recovery (Bottom-Right) */}
        <div
          onMouseEnter={() => setActiveNode("recover")}
          onMouseLeave={() => setActiveNode(null)}
          className={`absolute bottom-0 right-0 z-20 p-3 sm:p-3.5 rounded-2xl bg-white/95 backdrop-blur-md border transition-all duration-300 text-left max-w-[195px] cursor-pointer ${
            activeNode === "recover"
              ? "border-[#FF5A1F] shadow-xl shadow-[#FF5A1F]/20 scale-105"
              : "border-neutral-200/90 shadow-md hover:shadow-lg hover:border-neutral-300"
          }`}
        >
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-8 h-8 rounded-xl bg-orange-50 text-[#FF5A1F] grid place-items-center shrink-0">
              <RefreshCw className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-neutral-900 leading-tight">Rapid Recovery</div>
              <div className="text-[10px] text-neutral-500 font-medium">Zero-downtime</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-[#FF5A1F] pl-1 pt-1 border-t border-black/5">
            <span>Verified Baseline</span>
            <RefreshCw className="w-3 h-3 text-[#FF5A1F] animate-spin" style={{ animationDuration: "6s" }} />
          </div>
        </div>

      </div>

      {/* Mobile Grid Fallback for the 4 Cards (visible only on small screens) */}
      <div className="grid grid-cols-2 gap-2 mt-4 sm:hidden text-left">
        <div className="p-2.5 rounded-xl bg-white border border-neutral-200 shadow-xs flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#FF5A1F] shrink-0" />
          <div>
            <div className="text-[11px] font-bold text-neutral-900 leading-none">Threat Detection</div>
            <div className="text-[9px] text-neutral-500">Real-time analysis</div>
          </div>
        </div>
        <div className="p-2.5 rounded-xl bg-white border border-neutral-200 shadow-xs flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#FF5A1F] shrink-0" />
          <div>
            <div className="text-[11px] font-bold text-neutral-900 leading-none">Self-Healing</div>
            <div className="text-[9px] text-neutral-500">Automated runbooks</div>
          </div>
        </div>
        <div className="p-2.5 rounded-xl bg-white border border-neutral-200 shadow-xs flex items-center gap-2">
          <Database className="w-4 h-4 text-[#FF5A1F] shrink-0" />
          <div>
            <div className="text-[11px] font-bold text-neutral-900 leading-none">Workload Isolation</div>
            <div className="text-[9px] text-neutral-500">Instant containment</div>
          </div>
        </div>
        <div className="p-2.5 rounded-xl bg-white border border-neutral-200 shadow-xs flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-[#FF5A1F] shrink-0" />
          <div>
            <div className="text-[11px] font-bold text-neutral-900 leading-none">Rapid Recovery</div>
            <div className="text-[9px] text-neutral-500">Zero-downtime</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuardianHeroAnimation;
